<script>
import { formatNumber, getNumber } from '@utils/Format'
import { prependCurrencyMask } from '@modules/consolidator/utils/Currency/Currency'
import { customSort as baseCustomSort } from '@utils/Sort'
import { decimalLengthQuantity } from '@utils/Asset'
import { FORMAT_ORIGEM } from '@constants/Text'
import { light_mode } from '@constants/Color'
import EventIcon from '@modules/consolidator/components/event-icon/event-icon.vue'
import TableRowActions from '@modules/consolidator/components/table-row-actions/table-row-actions.vue'
import IaSuggestionsPendingBadge from '@modules/crm/components/ia-suggestions-pending-badge/ia-suggestions-pending-badge.vue'
import { CONSOLIDATOR_TABLE_CUSTOM_SORT_FIELDS } from '@modules/consolidator/constants/Text'
import {
  normalizeConsolidatorTableHeaders,
  buildMovementRowActions,
} from '@modules/consolidator/utils/table/table'

export default {
  name: 'MovimentacoesTable',
  components: {
    EventIcon,
    TableRowActions,
    IaSuggestionsPendingBadge,
  },
  props: {
    asset: {
      type: Object,
      default: () => ({}),
    },
    // Contagem de sugestoes pendentes POR MOVIMENTACAO. `null` = fila nao apurada; a linha nao
    // afirma nada, em vez de afirmar zero.
    sugestoesPorMovimentacao: {
      type: Object,
      default: null,
    },
    search: {
      type: String,
      default: '',
    },
    sortBy: {
      type: Array,
      default: () => ['data_cotizacao'],
    },
    sortDesc: {
      type: Array,
      default: () => [true],
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showAlertFillPu: {
      type: Boolean,
      default: false,
    },
    idAssetAlertFillPu: {
      type: [Number, String],
      default: null,
    },
    showAssetNameColumn: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    /**
     * Restringe as colunas desenhadas aos `value` desta lista, na ordem do
     * catálogo. `null` (o default) mantém as nove de sempre — as duas telas que
     * montam esta tabela seguem idênticas.
     *
     * Nasceu no cartão do chat (`consolidator/ia/carteira-movimentacoes-card.vue`,
     * spec-009 T015): o contrato do cartão tem cinco campos por movimentação, e
     * as nove colunas não cabem na bolha de 720px — medido em 27/08, a coluna
     * `Valor Total` ficava CORTADA fora do cartão, e ela é o número que o
     * consultor foi ler. Restringir é melhor que encolher: coluna sem dado
     * desenhava célula vazia e ainda roubava largura das que têm.
     */
    visibleColumns: {
      type: Array,
      default: null,
    },
    /**
     * Desenha TODOS os `items` de uma vez, sem paginador. `false` (o default)
     * mantém a paginação de 10 do BaseTable — as duas telas que montam esta
     * tabela seguem idênticas.
     *
     * Nasceu no cartão do chat (`consolidator/ia/carteira-movimentacoes-card.vue`),
     * como a `visibleColumns` acima, e pela mesma classe de motivo: medido em
     * 27/08, o cartão recebia 20 movimentações, desenhava 10 e o rodapé da casca
     * dizia "Mostrando 20 de 46" — número que não correspondia ao que a bolha
     * mostrava (D-G1 do design-conformance da spec-009). As outras 10 ficavam
     * atrás de um paginador, e vinham junto um `v-select` "Mostrar [10]" e um
     * segundo par de contagem ("Exibindo N de N") brigando com o da casca.
     *
     * O corte vinha do page-size default do BaseTable (`tableItemsPerPage: 10`,
     * `_base-table.vue:42`), que vale quando ninguém passa `items-per-page`.
     */
    showAllItems: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formatNumber,
      getNumber,
      FORMAT_ORIGEM,
      warningColor: light_mode['yellow-dark'],
    }
  },
  computed: {
    // Mapa em vez de funcao no template: o template reavalia a funcao a cada re-render, e a
    // tabela do Extrato tem centenas de linhas. Mesmo padrao do `sugestoesPendentesById`.
    sugestoesPorLinha() {
      return this.sugestoesPorMovimentacao || {}
    },
    hasAssetSelected() {
      return !!(this.asset && this.asset.id)
    },
    tableItems() {
      return Array.isArray(this.items) ? this.items : []
    },
    /**
     * Os atributos que desligam a paginação — e SÓ quando `showAllItems` pede.
     *
     * ★ Objeto vazio no default, e não `:items-per-page="undefined"`: o
     * BaseTable decide quem é dono do page-size por `'items-per-page' in
     * $attrs` (`_base-table.vue:96`), e um binding com `undefined` deixa a
     * CHAVE no `$attrs` — as telas passariam a ser "donas" de um valor que
     * ninguém definiu. Com `v-bind` de um objeto vazio, nada entra.
     *
     * `-1` é o "todos" do v-data-table; `hide-default-footer` some com o
     * paginador nativo E com o rodapé próprio do BaseTable
     * (`_base-table.vue:273`), que é quem desenha o "Mostrar [N]".
     */
    atributosSemPaginacao() {
      if (!this.showAllItems) return {}
      return { 'items-per-page': -1, 'hide-default-footer': true }
    },
    headers() {
      const baseHeaders = []
      const hasAsset = !!(this.asset && this.asset.id)
      const shouldIncludeAssetColumn = !hasAsset || this.showAssetNameColumn

      if (shouldIncludeAssetColumn) {
        baseHeaders.push({ text: 'Ativo', value: 'nome_ativo' })
      }

      baseHeaders.push(
        { text: 'Tipo de ordem', value: 'operacao' },
        { text: 'Data competência', value: 'data' },
        { text: 'Data cotização', value: 'data_cotizacao' },
        { text: 'Quantidade', value: 'quantidade' },
        { text: 'Preço Unitário', value: 'valor_unitario' },
        { text: 'Valor Total', value: 'valor' },
        { text: 'Data registro', value: 'data_registro' },
        { text: 'Origem', value: 'origem', default: 'Manual' }
      )

      return normalizeConsolidatorTableHeaders({
        headers: this.applyVisibleColumns(baseHeaders),
        includeActions: !this.readOnly,
        // A coluna do ícone só entra quando pedida; fora dela, `undefined`
        // preserva o default do helper (`includeIcon !== false`).
        includeIcon: this.visibleColumns
          ? this.visibleColumns.indexOf('icon') >= 0
          : undefined,
      })
    },
    emptyStateAssetLabel() {
      if (!this.showAssetNameColumn) {
        return ''
      }

      const namesList = this.getEmptyStateAssetNames()
      if (!namesList.length) {
        return 'Ativos'
      }

      return `Ativo: ${namesList[0]}`
    },
  },
  methods: {
    applyVisibleColumns(headers) {
      if (!this.visibleColumns) return headers
      return headers.filter(
        (header) => this.visibleColumns.indexOf(header.value) >= 0
      )
    },
    getEmptyStateAssetNames() {
      const hasAssetSelected = this.asset && this.asset.id
      if (hasAssetSelected) {
        const assetText = this.getAssetNameText({ item: this.asset })
        return assetText ? [assetText] : []
      }
      return []
    },
    quantityText({ item }) {
      if (item && item.quantidade_f) {
        return item.quantidade_f
      }

      if (item && item.quantidade) {
        return this.formatNumber(item.quantidade, decimalLengthQuantity(item))
      }

      return '-'
    },
    unitPriceText({ item }) {
      return prependCurrencyMask({
        value: item && item.valor_unitario,
        currency: this.resolveCurrency(item),
      })
    },
    resolveCurrency(item) {
      return (
        (item && item.moeda) ||
        (this.asset && this.asset.moeda) ||
        (item && item.valor) ||
        ''
      )
    },
    customSort(items, index, isDec) {
      return baseCustomSort(
        items,
        index,
        isDec,
        CONSOLIDATOR_TABLE_CUSTOM_SORT_FIELDS
      )
    },
    openMovimentacoes(asset) {
      this.$emit('open-movimentacoes', asset)
    },
    getMovementActions({ item }) {
      return buildMovementRowActions({
        item,
        onEdit: this.openEditAporteResgate,
        onRemove: this.openModalRemove,
      })
    },
    openEditAporteResgate(item) {
      this.$emit('edit-aporte-resgate', item)
    },
    openModalRemove(item) {
      this.$emit('remove-movimentacao', item)
    },
    shouldShowUnitPriceAlert(item) {
      if (!this.showAlertFillPu) {
        return false
      }
      if (!item) {
        return false
      }
      if (item.id_ativo_cliente !== this.idAssetAlertFillPu) {
        return false
      }
      const unitPrice = this.getNumber(item.valor_unitario)
      return !unitPrice
    },
    getAssetNameText({ item }) {
      const assetName =
        (item && item.nome_ativo) || (this.asset || {}).nome_ativo
      const brokerName =
        (item && item.corretora) || (this.asset || {}).corretora

      // O traço separa o nome da corretora; sem corretora ele ficava órfão no
      // fim do rótulo ("Saldo em Conta -"). Na tela sempre há corretora, então
      // isto só aparece em quem monta a tabela sem ela — o cartão do chat.
      if (assetName === 'Saldo em Conta' && brokerName) {
        return `${assetName} - ${brokerName}`
      }

      return assetName || ''
    },
  },
}
</script>

<template>
  <div class="movimentacoes-table">
    <BaseTable
      v-bind="atributosSemPaginacao"
      :title="title"
      :custom-sort="customSort"
      :headers="headers"
      :items="tableItems"
      :loading="loading"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :search-value="search"
      disable-margin-top
    >
      <template v-slot:no-data>
        <div class="full-center my-10">
          <p v-if="showAssetNameColumn" class="text-bold">
            {{ emptyStateAssetLabel }}
          </p>

          <template v-if="hasAssetSelected">
            <p class="text-bold">
              Este ativo não possui nenhuma movimentação cadastrada.
            </p>
            <p>
              Para conseguir calcular a rentabilidade dele, é necessário ter ao
              menos uma movimentação cadastrada.
            </p>
            <BaseButton primary @click="openMovimentacoes(asset)">
              Adicionar movimentação
            </BaseButton>
          </template>

          <template v-else>
            <p class="text-bold">
              Nenhuma movimentação encontrada para os filtros selecionados.
            </p>
            <p>Selecione apenas um ativo para adicionar uma movimentação.</p>
          </template>
        </div>
      </template>

      <template v-slot:item.icon="{ item }">
        <EventIcon :event="item" />
      </template>

      <template
        v-if="!asset || !asset.id || showAssetNameColumn"
        v-slot:item.nome_ativo="{ item }"
      >
        <div>
          {{ getAssetNameText({ item }) }}
        </div>
      </template>

      <template v-slot:item.operacao="{ item }">
        <div class="d-flex align-items-center az-gap-small">
          {{ item.operacao }}
          <IaSuggestionsPendingBadge
            v-if="sugestoesPorLinha[item.id_aplicacao]"
            :pendentes="sugestoesPorLinha[item.id_aplicacao]"
            data-test="mov-sugestoes-ia"
            @revisar="$emit('revisar-sugestoes', item)"
          />
        </div>
      </template>

      <template v-slot:item.origem="{ item }">
        {{ FORMAT_ORIGEM[item.origem] || item.origem }}
      </template>

      <template v-slot:item.quantidade="{ item }">
        {{ quantityText({ item }) }}
      </template>

      <template v-slot:item.valor_unitario="{ item }">
        {{ unitPriceText({ item }) }}
      </template>

      <template v-slot:item.action="{ item }">
        <div class="d-flex justify-content-center">
          <div v-if="shouldShowUnitPriceAlert(item)">
            <v-tooltip max-width="300" left>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <icon-svg name="warning" :color="warningColor" />
                </div>
              </template>
              <span>
                Para calcular a rentabilidade, é necessário preencher o preço
                unitário e quantidade desta movimentação.
              </span>
            </v-tooltip>
          </div>

          <TableRowActions :actions="getMovementActions({ item })" />
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<style lang="sass">
.movimentacoes-table
  width: 100%
</style>
